import Gooey, {
    Component,
    calc,
    Dyn,
    dynGet,
    dynSet,
    dynSubscribe,
    field,
    model,
    ref,
} from '@srhazi/gooey';
import './marquee-pane.scss';

const safeMod = (a: number, b: number) => ((a % b) + b) % b;

export const MarqueePane: Component<{
    children?: JSX.Node | JSX.Node[];
    width?: Dyn<string | number>;
    height?: Dyn<string | number>;
    gapX?: Dyn<number>;
    gapY?: Dyn<number>;
    scrollEven?: Dyn<number>;
    scrollOdd?: Dyn<number>;
    angle?: Dyn<number>;
}> = (
    {
        children,
        width = 'auto',
        height = 'auto',
        gapX = 10,
        gapY = 10,
        scrollEven = 47,
        scrollOdd = -50,
        angle = Math.PI / 6,
    },
    { onMount }
) => {
    const evenShift = field(0);
    const oddShift = field(0);

    const childContainerRef = ref<HTMLDivElement>();
    const elRef = ref<HTMLDivElement>();
    const offscreenElField = field<HTMLDivElement | undefined>(undefined);
    const scrollerRef = ref<HTMLDivElement>();

    const rawDimensions = model({
        width: 0,
        height: 0,
    });

    const childDimensions = model<{
        width: number | undefined;
        height: number | undefined;
    }>({
        width: undefined,
        height: undefined,
    });

    const nodeInfo = calc(() => {
        const offscreenEl = offscreenElField.get();
        if (
            !offscreenEl ||
            rawDimensions.width === 0 ||
            rawDimensions.height === 0
        ) {
            return undefined;
        }
        let rect = childDimensions;
        if (rect.width === undefined || rect.height === undefined) {
            updateChildDimensions();
        }
        const bounds = {
            width: Math.ceil(rect.width! + dynGet(gapX)),
            height: Math.ceil(rect.height! + dynGet(gapY)),
        };
        const rotatedWidth =
            rawDimensions.height * Math.abs(Math.sin(dynGet(angle))) +
            rawDimensions.width * Math.abs(Math.cos(dynGet(angle)));
        const rotatedHeight =
            rawDimensions.height * Math.abs(Math.cos(dynGet(angle))) +
            rawDimensions.width * Math.abs(Math.sin(dynGet(angle)));
        let numNodesH = Math.ceil(rotatedWidth / bounds.width) + 2;
        let numNodesV = Math.ceil(rotatedHeight / bounds.height) + 1;
        // Need an even number of nodes in order to keep things centered correctly
        if (numNodesH & 1) {
            numNodesH++;
        }
        if (numNodesV & 1) {
            numNodesV++;
        }
        const totalWidth = numNodesH * bounds.width - dynGet(gapX);
        const totalHeight = numNodesV * bounds.height - dynGet(gapY);
        const totalShiftX = -(totalWidth - rawDimensions.width) / 2;
        const totalShiftY = -(totalHeight - rawDimensions.height) / 2;
        const evenNode = childContainerRef.current?.cloneNode(
            true
        ) as HTMLSpanElement;
        const oddNode = childContainerRef.current?.cloneNode(
            true
        ) as HTMLSpanElement;
        evenNode.className = 'marquee-pane_item--even';
        oddNode.className = 'marquee-pane_item--odd';
        const labelNodes: HTMLSpanElement[] = [];
        for (let x = 0; x < numNodesH; ++x) {
            for (let y = 0; y < numNodesV; ++y) {
                const toClone = y & 1 ? oddNode : evenNode;
                const cloned = toClone.cloneNode(true) as HTMLSpanElement;
                cloned.style.setProperty(
                    '--marquee-pane_item-col',
                    x.toString()
                );
                cloned.style.setProperty(
                    '--marquee-pane_item-row',
                    y.toString()
                );
                labelNodes.push(cloned);
            }
        }
        return { bounds, labelNodes, totalShiftX, totalShiftY };
    });

    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            if (entry.target === elRef.current) {
                if (entry.borderBoxSize) {
                    const box = entry.borderBoxSize[0];
                    rawDimensions.width = box.inlineSize;
                    rawDimensions.height = box.blockSize;
                } else {
                    rawDimensions.width = entry.contentRect.width;
                    rawDimensions.height = entry.contentRect.height;
                }
            }
        }
    });
    const updateChildDimensions = () => {
        if (childContainerRef.current) {
            const bounds = childContainerRef.current.getBoundingClientRect();
            childDimensions.width = bounds.width;
            childDimensions.height = bounds.height;
        }
    };
    const mutationObserver = new MutationObserver((entries) => {
        updateChildDimensions();
    });

    let animationHandle: null | number = null;
    let lastTickMs: null | number = null;
    const tick = (ms: number) => {
        if (lastTickMs === null) {
            lastTickMs = ms;
        }
        let dt = (ms - lastTickMs) / 1000;
        lastTickMs = ms;
        const nodeWidth = nodeInfo.get()?.bounds.width;
        let es = evenShift.get() + dynGet(scrollEven) * dt;
        let os = oddShift.get() + dynGet(scrollOdd) * dt;
        if (nodeWidth) {
            es = safeMod(es, nodeWidth);
            os = safeMod(os, nodeWidth);
        }
        evenShift.set(es);
        oddShift.set(os);

        animationHandle = requestAnimationFrame(tick);
    };

    onMount(() => {
        if (elRef.current) {
            resizeObserver.observe(elRef.current);
        }
        if (childContainerRef.current) {
            mutationObserver.observe(childContainerRef.current, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        }
        evenShift.set(0);
        oddShift.set(50);
        animationHandle = requestAnimationFrame((ms) => {
            tick(ms);
        });
        return () => {
            mutationObserver.disconnect();
            resizeObserver.disconnect();
            if (animationHandle) {
                cancelAnimationFrame(animationHandle);
                animationHandle = null;
            }
        };
    });

    // TODO: bug in gooey where cssprop: props are not bound to dynamic values
    return (
        <div
            ref={elRef}
            class="marquee-pane"
            style={calc(() => {
                const widthRaw = dynGet(width);
                const widthStr =
                    typeof widthRaw === 'string' ? widthRaw : `${widthRaw}px`;
                const heightRaw = dynGet(height);
                const heightStr =
                    typeof heightRaw === 'string'
                        ? heightRaw
                        : `${heightRaw}px`;
                return [
                    `--marquee-pane_width: ${widthStr}`,
                    `--marquee-pane_height: ${heightStr}`,
                    `--marquee-pane_calc_width: ${rawDimensions.width}px`,
                    `--marquee-pane_calc_height: ${rawDimensions.height}px`,
                ].join(';');
            })}
        >
            <div class="marquee-pane_overflow">
                <div class="marquee-pane_scroller" ref={scrollerRef}>
                    <div
                        class="marquee-pane_offscreen"
                        ref={(el) => offscreenElField.set(el)}
                    >
                        <span ref={childContainerRef}>{children}</span>
                    </div>
                    <div
                        role="presentation"
                        aria-hidden="true"
                        class="marquee-pane_shifter"
                        style={calc(() => {
                            const info = nodeInfo.get();
                            if (!info) {
                                return '';
                            }
                            return [
                                `--marquee-pane_shifter_angle: ${dynGet(
                                    angle
                                )}`,
                                `--marquee-pane_total-shift-x: ${info.totalShiftX}px`,
                                `--marquee-pane_total-shift-y: ${info.totalShiftY}px`,
                                `--marquee-pane_even-shift: ${evenShift.get()}px`,
                                `--marquee-pane_odd-shift: ${oddShift.get()}px`,
                                `--marquee-pane_item-width: ${info.bounds.width}px`,
                                `--marquee-pane_item-height: ${info.bounds.height}px`,
                            ].join(';');
                        })}
                    >
                        {calc(() => nodeInfo.get()?.labelNodes)}
                    </div>
                </div>
            </div>
        </div>
    );
};
